'use client';

import { useId } from 'react';
import type { CSSProperties } from 'react';
import { RiInformationLine } from '@remixicon/react';
import { useLocale, useTranslations } from 'next-intl';

import { ProductSection, SectionHead } from '@/components/product-section';
import { GaugeArc } from '@/components/ui/gauge-arc';
import { hl } from '@/lib/rich-text';
import { STORE_INTEGRATIONS } from '@/lib/site-integrations';

import './campaigns.css';

/**
 * "Reklamın yanında mağazanın gerçeği" — ürün performansı + katalog sağlığı.
 *
 * Doğrulanmış sınırlarıyla (14 Eki 2026 ürün dökümü):
 *   · ürün performansı mağaza satışını reklam harcamasının YANINA koyuyor,
 *     ikisini birleştirmiyor; "mağaza ROAS" organik satışı da içeriyor —
 *     sütun başlığının notu bunu söylüyor,
 *   · katalog sağlığı kural tabanlı bir puan (100 − cezalar,
 *     `catalog-health.ts`); AI'ın metin düzeltmesi yalnız Shopify'a geri
 *     yazılıyor, diğer mağazalarda kopyalanıyor.
 * Puan göstergesi `GaugeArc` (DESIGN.md §3 — sohbetteki katalog kartıyla
 * aynı çizim). Rakamlar örnek.
 */

const PRODUCTS = [
  { key: 'serum', spend: 420, revenue: 2310 },
  { key: 'cream', spend: 260, revenue: 610 },
  { key: 'mask', spend: 0, revenue: 380 },
] as const;

const ISSUES = ['noImage', 'outOfStock', 'shortDescription', 'shortTitle'] as const;
const ISSUE_COUNT: Record<(typeof ISSUES)[number], number> = {
  noImage: 3,
  outOfStock: 2,
  shortDescription: 7,
  shortTitle: 4,
};
const SCORE = 72;

export function CampaignStore() {
  const t = useTranslations('featuresCampaigns.store');
  const locale = useLocale();
  const uid = useId();

  const money = new Intl.NumberFormat(locale, {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 0,
  });
  const ratio = new Intl.NumberFormat(locale, { minimumFractionDigits: 1, maximumFractionDigits: 1 });

  return (
    <ProductSection tone="gray" labelledBy={`${uid}-title`}>
      <SectionHead
        id={`${uid}-title`}
        eyebrow={t('eyebrow')}
        title={t.rich('title', { hl })}
        lead={t('lead')}
      />

      <div className="cst">
        <div className="cst-card">
          <p className="cst-card-title">{t('products.title')}</p>
          <div className="cl-scroll">
            <table className="cst-table">
              <thead>
                <tr>
                  <th scope="col">{t('products.cols.product')}</th>
                  <th scope="col" className="cl-num">
                    {t('products.cols.spend')}
                  </th>
                  <th scope="col" className="cl-num">
                    {t('products.cols.revenue')}
                  </th>
                  <th scope="col" className="cl-num">
                    {t('products.cols.roas')}
                    <sup className="cst-sup">*</sup>
                  </th>
                </tr>
              </thead>
              <tbody>
                {PRODUCTS.map((row) => (
                  <tr key={row.key}>
                    <th scope="row">{t(`products.rows.${row.key}`)}</th>
                    <td className="cl-num">{row.spend > 0 ? money.format(row.spend) : '—'}</td>
                    <td className="cl-num">{money.format(row.revenue)}</td>
                    <td className="cl-num">
                      {row.spend > 0 ? `${ratio.format(row.revenue / row.spend)}×` : t('products.organic')}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="cst-foot">* {t('products.foot')}</p>
        </div>

        <div className="cst-card cst-health">
          <p className="cst-card-title">{t('health.title')}</p>
          <div className="cst-health-body">
            <div className="cst-gauge">
              <GaugeArc
                value={SCORE / 100}
                format={(value) =>
                  new Intl.NumberFormat(locale, { maximumFractionDigits: 0 }).format(value * 100)
                }
                unit=""
                label={t('health.score', { score: SCORE })}
              />
              <p className="cst-gauge-label">{t('health.score', { score: SCORE })}</p>
            </div>
            <ul className="cst-issues">
              {ISSUES.map((key) => (
                <li key={key} className="cst-issue">
                  <span className="cst-issue-name">{t(`health.issues.${key}`)}</span>
                  <span className="cst-issue-count">
                    {new Intl.NumberFormat(locale).format(ISSUE_COUNT[key])}
                  </span>
                </li>
              ))}
            </ul>
          </div>
          <p className="cst-foot">{t('health.foot')}</p>
        </div>
      </div>

      <div className="cst-stores">
        <p className="cst-stores-label">{t('storesLabel', { count: STORE_INTEGRATIONS.length })}</p>
        <ul className="cst-store-list">
          {STORE_INTEGRATIONS.map((store) => (
            <li key={store.key} className="cst-store">
              {/* eslint-disable-next-line @next/next/no-img-element */}
              <img
                className="cst-store-mark"
                src={store.mark}
                alt=""
                width={store.w}
                height={store.h}
                loading="lazy"
                decoding="async"
                style={{ '--mark-scale': store.scale } as CSSProperties}
              />
              {store.name}
            </li>
          ))}
        </ul>
      </div>

      <p className="ps-note">
        <RiInformationLine size={16} aria-hidden="true" />
        {t('note')}
      </p>
    </ProductSection>
  );
}
